import React, { useState, useEffect } from 'react';
import { Eye, Plus, ExternalLink } from 'lucide-react';
import { maintenanceService } from '../../services/maintenanceService';
import type { MaintenanceContract } from '../../types/maintenance';
import HoursProgressBar from './HoursProgressBar';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  active: { label: 'Activo', color: 'bg-green-100 text-green-700' },
  paused: { label: 'Pausado', color: 'bg-yellow-100 text-yellow-700' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};

interface ContractCardProps {
  contract: MaintenanceContract;
  onViewDetail: (id: string) => void;
  onAddTask: (id: string) => void;
}

const ContractCard: React.FC<ContractCardProps> = ({ contract, onViewDetail, onAddTask }) => {
  const [hours, setHours] = useState({ total: 0, support: 0, improvement: 0, remaining: 0, extra: 0 });
  const currentMonth = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    maintenanceService.getMonthlyHours(contract.id, currentMonth)
      .then(setHours)
      .catch(console.error);
  }, [contract.id]);

  const statusCfg = STATUS_CONFIG[contract.status] || STATUS_CONFIG.active;

  const handleCopyPortalLink = () => {
    const url = `${window.location.origin}/portal/mantenimiento/${contract.portal_token}`;
    navigator.clipboard.writeText(url);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{contract.client_name}</h3>
          <p className="text-sm text-gray-500">{contract.plan_name}</p>
        </div>
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
          {statusCfg.label}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
        <div>
          <span className="text-gray-500">Precio</span>
          <p className="font-semibold">{contract.monthly_price}€/mes</p>
        </div>
        <div>
          <span className="text-gray-500">Horas</span>
          <p className="font-semibold">{contract.monthly_hours}h/mes</p>
        </div>
        <div>
          <span className="text-gray-500">Pago</span>
          <p className="font-semibold">{contract.payment_method}</p>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-xs text-gray-500 mb-1">Horas del mes actual</p>
        <HoursProgressBar
          used={hours.total}
          included={contract.monthly_hours}
          extra_price={contract.extra_hour_price}
        />
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
        <button
          onClick={() => onViewDetail(contract.id)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
        >
          <Eye className="w-4 h-4" />
          Ver detalle
        </button>
        <button
          onClick={() => onAddTask(contract.id)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg"
        >
          <Plus className="w-4 h-4" />
          Tarea
        </button>
        <button
          onClick={handleCopyPortalLink}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 rounded-lg ml-auto"
          title="Copiar enlace portal cliente"
        >
          <ExternalLink className="w-4 h-4" />
          Portal
        </button>
      </div>
    </div>
  );
};

export default ContractCard;
